import { useState } from 'react';
import { Link } from 'react-router-dom';

interface Category {
  id: string;
  name: string;
  processes: Array<{ id: string; name: string; rating: number; responses: number }>;
}

export default function Home() {
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'student' | 'academic' | 'admin' | 'finance' | 'governance'>('student');
  const [searchQuery, setSearchQuery] = useState('');

  // Helper function to generate random response count (max 5000)
  const getResponseCount = (processId: string) => {
    const counts: Record<string, number> = {
      // Academic & Student Lifecycle - realistic counts under 5000
      'course-registration': 847,
      'exam-registration': 934,
      'grade-change': 676,
      'transcript-request': 1187,
      'course-withdrawal': 679,
      
      // Student Financial Processes - realistic counts
      'fee-payment': 521,
      'scholarship-application': 345,
      'refund-request': 428,
      'payment-plan': 355,
      'work-study': 892,
      'heritage-scholarship': 456,
      
      // Facilities & Campus Services - realistic counts
      'facility-booking': 234,
      'maintenance-request': 331,
      'visitor-registration': 442,
      'parking-permit': 260,
      'hall-residence': 892,
      'staff-quarters': 378,
      'exeat-request': 456,
      
      // Research & Partnerships - realistic counts
      'research-proposal': 167,
      'grant-application': 245,
      'partnership-agreement': 219,
      'ethics-approval': 289,
      
      // HR and Staff processes - realistic counts under 5000
      'staff-onboarding': 456,
      'staff-payroll': 598,
      'leave-application': 476,
      'recruitment': 376,
      'performance-review': 354,
      'promotion': 432,
      'salary-advance': 487,
      'honorarium-payment': 343,
      'overtime-claim': 434,
      'staff-loan': 376,
      'end-of-service': 243,
      
      // Finance processes - realistic counts under 5000
      'budget-approval': 432,
      'purchase-request': 543,
      'expense-reimbursement': 487,
      'vendor-payment': 534,
      'financial-reporting': 443,
      'vendor-selection': 376,
      'goods-receipt': 509,
      'contract-management': 454,
      
      // Asset management - realistic counts under 5000
      'asset-registration': 389,
      'asset-maintenance': 443,
      'inventory-audit': 332,
      'asset-disposal': 321,
      
      // Governance & IT - realistic counts under 5000
      'internal-audit': 334,
      'risk-assessment': 434,
      'compliance-review': 356,
      'policy-approval': 445,
      'it-support': 654,
      'system-access': 545,
      'software-deployment': 465,
      'data-backup': 476,
      'event-approval': 498,
      
      // Communication - realistic counts under 5000
      'publication-request': 343,
      'media-engagement': 276,
      'alumni-engagement': 409,
      
      // New Teaching & Learning processes
      'course-allocation': 434,
      'lecture-timetable': 467,
      'exam-setting': 532,
      'result-processing': 476,
      'curriculum-review': 376,
      
      // New Research processes
      'research-grant': 334,
      'research-publication': 356,
      
      // New Academic Development processes
      'conference-attendance': 267,
      'sabbatical-leave': 223,
      'faculty-training': 389,
      'academic-promotion': 334,
      
      // New Partnerships processes
      'mou-signing': 245,
    };
    return counts[processId] || Math.floor(Math.random() * 500) + 100;
  };

  // Student Services Group
  const studentCategories: Category[] = [
    {
      id: 'academic-lifecycle',
      name: 'Academic & Student Lifecycle',
      processes: [
        { id: 'course-registration', name: 'Course Registration Process', rating: 2.1, responses: getResponseCount('course-registration') },
        { id: 'exam-registration', name: 'Exam Registration Process', rating: 3.8, responses: getResponseCount('exam-registration') },
        { id: 'grade-change', name: 'Change of Grade Process', rating: 3.2, responses: getResponseCount('grade-change') },
        { id: 'transcript-request', name: 'Transcript Request Process', rating: 4.1, responses: getResponseCount('transcript-request') },
        { id: 'course-withdrawal', name: 'Course Withdrawal Process', rating: 3.5, responses: getResponseCount('course-withdrawal') }
      ]
    },
    {
      id: 'student-financial',
      name: 'Student Financial Services',
      processes: [
        { id: 'fee-payment', name: 'Student Fee Payment Process', rating: 3.9, responses: getResponseCount('fee-payment') },
        { id: 'scholarship-application', name: 'Scholarship Application Process', rating: 3.6, responses: getResponseCount('scholarship-application') },
        { id: 'heritage-scholarship', name: 'Heritage Scholarship for Adventist Students', rating: 3.7, responses: getResponseCount('heritage-scholarship') },
        { id: 'work-study', name: 'Work Study Program Process', rating: 3.5, responses: getResponseCount('work-study') },
        { id: 'refund-request', name: 'Refund Request Process', rating: 2.8, responses: getResponseCount('refund-request') },
        { id: 'payment-plan', name: 'Payment Plan Setup Process', rating: 3.4, responses: getResponseCount('payment-plan') }
      ]
    },
    {
      id: 'student-accommodation',
      name: 'Accommodation & Campus Life',
      processes: [
        { id: 'hall-residence', name: 'Hall of Residence Process (On-Campus & Off-Campus)', rating: 2.9, responses: getResponseCount('hall-residence') },
        { id: 'exeat-request', name: 'Exeat Request Process (Short & Long Distance)', rating: 3.6, responses: getResponseCount('exeat-request') },
        { id: 'parking-permit', name: 'Parking Permit Process', rating: 3.8, responses: getResponseCount('parking-permit') }
      ]
    }
  ];

  // Academic & Research Group
  const academicCategories: Category[] = [
    {
      id: 'teaching-learning',
      name: 'Teaching & Learning',
      processes: [
        { id: 'course-allocation', name: 'Course Allocation Process', rating: 3.2, responses: getResponseCount('course-allocation') },
        { id: 'lecture-timetable', name: 'Lecture Timetable Creation Process', rating: 2.9, responses: getResponseCount('lecture-timetable') },
        { id: 'exam-setting', name: 'Exam Setting & Moderation Process', rating: 3.4, responses: getResponseCount('exam-setting') },
        { id: 'result-processing', name: 'Result Processing & Approval Process', rating: 3.1, responses: getResponseCount('result-processing') },
        { id: 'curriculum-review', name: 'Curriculum Review Process', rating: 3.6, responses: getResponseCount('curriculum-review') }
      ]
    },
    {
      id: 'research',
      name: 'Research & Innovation',
      processes: [
        { id: 'research-proposal', name: 'Research Proposal Approval Process', rating: 3.3, responses: getResponseCount('research-proposal') },
        { id: 'grant-application', name: 'Grant Application Process', rating: 3.5, responses: getResponseCount('grant-application') },
        { id: 'research-grant', name: 'Research Grant Management Process', rating: 3.4, responses: getResponseCount('research-grant') },
        { id: 'ethics-approval', name: 'Ethics Approval Process', rating: 3.7, responses: getResponseCount('ethics-approval') },
        { id: 'research-publication', name: 'Research Publication Support Process', rating: 3.8, responses: getResponseCount('research-publication') }
      ]
    },
    {
      id: 'academic-development',
      name: 'Academic Development',
      processes: [
        { id: 'conference-attendance', name: 'Conference Attendance Approval Process', rating: 3.3, responses: getResponseCount('conference-attendance') },
        { id: 'sabbatical-leave', name: 'Sabbatical Leave Application Process', rating: 3.5, responses: getResponseCount('sabbatical-leave') },
        { id: 'faculty-training', name: 'Faculty Training & Development Process', rating: 3.7, responses: getResponseCount('faculty-training') },
        { id: 'academic-promotion', name: 'Academic Promotion Process', rating: 3.1, responses: getResponseCount('academic-promotion') }
      ]
    },
    {
      id: 'partnerships',
      name: 'Partnerships & Engagement',
      processes: [
        { id: 'partnership-agreement', name: 'Partnership Agreement Process', rating: 3.9, responses: getResponseCount('partnership-agreement') },
        { id: 'mou-signing', name: 'MOU Signing Process', rating: 3.8, responses: getResponseCount('mou-signing') },
        { id: 'publication-request', name: 'Publication Request Process', rating: 3.5, responses: getResponseCount('publication-request') },
        { id: 'media-engagement', name: 'Media Engagement Process', rating: 3.9, responses: getResponseCount('media-engagement') },
        { id: 'alumni-engagement', name: 'Alumni Engagement Process', rating: 4.0, responses: getResponseCount('alumni-engagement') }
      ]
    }
  ];

  // Administrative Operations Group
  const adminCategories: Category[] = [
    {
      id: 'hr',
      name: 'Human Resource Management',
      processes: [
        { id: 'staff-onboarding', name: 'Staff Onboarding Process', rating: 2.3, responses: getResponseCount('staff-onboarding') },
        { id: 'staff-payroll', name: 'Staff Payroll Process', rating: 4.0, responses: getResponseCount('staff-payroll') },
        { id: 'leave-application', name: 'Leave Application Process', rating: 3.9, responses: getResponseCount('leave-application') },
        { id: 'recruitment', name: 'Staff Recruitment Process', rating: 3.4, responses: getResponseCount('recruitment') },
        { id: 'performance-review', name: 'Performance Review Process', rating: 3.6, responses: getResponseCount('performance-review') },
        { id: 'promotion', name: 'Staff Promotion Process', rating: 3.2, responses: getResponseCount('promotion') },
        { id: 'salary-advance', name: 'Salary Advance Request Process', rating: 3.5, responses: getResponseCount('salary-advance') },
        { id: 'honorarium-payment', name: 'Honorarium Payment Process', rating: 3.7, responses: getResponseCount('honorarium-payment') },
        { id: 'overtime-claim', name: 'Overtime Claim Process', rating: 3.4, responses: getResponseCount('overtime-claim') },
        { id: 'staff-loan', name: 'Staff Loan Application Process', rating: 3.3, responses: getResponseCount('staff-loan') },
        { id: 'end-of-service', name: 'End of Service Benefits Process', rating: 3.6, responses: getResponseCount('end-of-service') }
      ]
    },
    {
      id: 'facilities',
      name: 'Facilities & Campus Services',
      processes: [
        { id: 'facility-booking', name: 'Facility Booking Process', rating: 3.7, responses: getResponseCount('facility-booking') },
        { id: 'maintenance-request', name: 'Maintenance Request Process', rating: 3.1, responses: getResponseCount('maintenance-request') },
        { id: 'visitor-registration', name: 'Visitor Registration Process', rating: 4.2, responses: getResponseCount('visitor-registration') },
        { id: 'staff-quarters', name: 'Staff Quarters Allocation Process', rating: 3.3, responses: getResponseCount('staff-quarters') },
        { id: 'event-approval', name: 'Event Approval Process', rating: 3.7, responses: getResponseCount('event-approval') }
      ]
    }
  ];

  // Finance & Procurement Group
  const financeCategories: Category[] = [
    {
      id: 'finance',
      name: 'Finance & Treasury',
      processes: [
        { id: 'budget-approval', name: 'Budget Approval Process', rating: 3.5, responses: getResponseCount('budget-approval') },
        { id: 'expense-reimbursement', name: 'Expense Reimbursement Process', rating: 3.3, responses: getResponseCount('expense-reimbursement') },
        { id: 'vendor-payment', name: 'Vendor Payment Process', rating: 3.7, responses: getResponseCount('vendor-payment') },
        { id: 'financial-reporting', name: 'Financial Reporting Process', rating: 3.8, responses: getResponseCount('financial-reporting') }
      ]
    },
    {
      id: 'procurement',
      name: 'Procurement & Supply Chain',
      processes: [
        { id: 'purchase-request', name: 'Purchase Request Process', rating: 3.4, responses: getResponseCount('purchase-request') },
        { id: 'vendor-selection', name: 'Vendor Selection Process', rating: 3.6, responses: getResponseCount('vendor-selection') },
        { id: 'goods-receipt', name: 'Goods Receipt Process', rating: 3.9, responses: getResponseCount('goods-receipt') },
        { id: 'contract-management', name: 'Contract Management Process', rating: 3.7, responses: getResponseCount('contract-management') }
      ]
    },
    {
      id: 'inventory',
      name: 'Inventory & Asset Management',
      processes: [
        { id: 'asset-registration', name: 'Asset Registration Process', rating: 3.8, responses: getResponseCount('asset-registration') },
        { id: 'asset-maintenance', name: 'Asset Maintenance Process', rating: 3.5, responses: getResponseCount('asset-maintenance') },
        { id: 'inventory-audit', name: 'Inventory Audit Process', rating: 3.6, responses: getResponseCount('inventory-audit') },
        { id: 'asset-disposal', name: 'Asset Disposal Process', rating: 3.4, responses: getResponseCount('asset-disposal') }
      ]
    }
  ];

  // Governance & IT Group
  const governanceCategories: Category[] = [
    {
      id: 'governance',
      name: 'Governance, Audit & Risk',
      processes: [
        { id: 'internal-audit', name: 'Internal Audit Process', rating: 3.7, responses: getResponseCount('internal-audit') },
        { id: 'risk-assessment', name: 'Risk Assessment Process', rating: 3.5, responses: getResponseCount('risk-assessment') },
        { id: 'compliance-review', name: 'Compliance Review Process', rating: 3.6, responses: getResponseCount('compliance-review') },
        { id: 'policy-approval', name: 'Policy Approval Process', rating: 3.3, responses: getResponseCount('policy-approval') }
      ]
    },
    {
      id: 'ict',
      name: 'ICT & Digital Services',
      processes: [
        { id: 'it-support', name: 'IT Support Request Process', rating: 4.1, responses: getResponseCount('it-support') },
        { id: 'system-access', name: 'System Access Request Process', rating: 3.8, responses: getResponseCount('system-access') },
        { id: 'software-deployment', name: 'Software Deployment Process', rating: 3.6, responses: getResponseCount('software-deployment') },
        { id: 'data-backup', name: 'Data Backup Process', rating: 4.3, responses: getResponseCount('data-backup') }
      ]
    }
  ];

  const getCategoriesByTab = () => {
    switch (activeTab) {
      case 'student':
        return studentCategories;
      case 'academic':
        return academicCategories;
      case 'admin':
        return adminCategories;
      case 'finance':
        return financeCategories;
      case 'governance':
        return governanceCategories;
      default:
        return studentCategories;
    }
  };

  const currentCategories = getCategoriesByTab();

  // Filter processes based on search query
  const getFilteredCategories = () => {
    if (!searchQuery.trim()) {
      return currentCategories;
    }

    const query = searchQuery.toLowerCase();
    return currentCategories
      .map(category => ({
        ...category,
        processes: category.processes.filter(process =>
          process.name.toLowerCase().includes(query)
        )
      }))
      .filter(category => category.processes.length > 0);
  };

  const filteredCategories = getFilteredCategories();

  // Get all processes for search results
  const getAllProcesses = () => {
    const allCategories = [...studentCategories, ...academicCategories, ...adminCategories, ...financeCategories, ...governanceCategories];
    return allCategories.flatMap(category => 
      category.processes.map(process => ({
        ...process,
        categoryName: category.name
      }))
    );
  };

  const searchResults = searchQuery.trim() 
    ? getAllProcesses().filter(process => 
        process.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : [];

  const toggleCategory = (categoryId: string) => {
    setExpandedCategory(expandedCategory === categoryId ? null : categoryId);
  };

  // Helper function to render star rating
  const renderStars = (rating: number) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    return (
      <div className="flex items-center gap-0.5">
        {[...Array(fullStars)].map((_, i) => (
          <i key={`full-${i}`} className="ri-star-fill text-sm text-yellow-500"></i>
        ))}
        {hasHalfStar && <i className="ri-star-half-fill text-sm text-yellow-500"></i>}
        {[...Array(emptyStars)].map((_, i) => (
          <i key={`empty-${i}`} className="ri-star-line text-sm text-gray-300"></i>
        ))}
      </div>
    );
  };

  // Helper function to get rating color
  const getRatingColor = (rating: number) => {
    if (rating < 2.5) return 'text-red-600';
    if (rating < 3.5) return 'text-orange-600';
    if (rating < 4.0) return 'text-yellow-600';
    return 'text-green-600';
  };

  // Calculate category average rating and total responses
  const getCategoryStats = (category: Category) => {
    const totalResponses = category.processes.reduce((sum, p) => sum + p.responses, 0);
    const avgRating = category.processes.reduce((sum, p) => sum + p.rating, 0) / category.processes.length;
    return { totalResponses, avgRating };
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <div className="flex items-center justify-between gap-3 sm:gap-4">
            <div className="flex items-center gap-3 sm:gap-4">
              <img 
                src="https://static.readdy.ai/image/b26ccc3ae7fa45b3398224fbc482abd6/9b19fe2637e0feac9d25a7f594575d5a.png" 
                alt="Babcock University" 
                className="h-10 sm:h-12 w-auto"
              />
              <div>
                <h1 className="text-base sm:text-xl font-semibold text-gray-900">Process Discovery Platform</h1>
                <p className="text-xs sm:text-sm text-gray-500">Babcock University</p>
              </div>
            </div>
            <div className="px-3 sm:px-4 py-1.5 sm:py-2 bg-red-100 border-2 border-red-500 rounded-lg">
              <span className="text-xs sm:text-sm font-bold text-red-600 whitespace-nowrap">DEMO ONLY</span>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section with Instructions */}
      <section className="bg-gradient-to-r from-[#1C2E5D] to-[#284574] text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-16">
          <div className="max-w-3xl">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3 sm:mb-4">Help Us Improve University Processes</h2>
            <p className="text-lg text-blue-100 mb-6 sm:mb-8">
              Your experience matters! Share how processes actually work and help make them better for everyone.
            </p>
            
            {/* How it Works */}
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 sm:p-5 border border-white/20">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#E1C582] rounded-full flex items-center justify-center mb-2 sm:mb-3">
                  <span className="text-xl sm:text-2xl font-bold text-[#1C2E5D]">1</span>
                </div>
                <h3 className="font-semibold text-base sm:text-lg mb-1 sm:mb-2">Choose a Process</h3>
                <p className="text-xs sm:text-sm text-blue-100">Browse and select any process you've experienced</p>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 sm:p-5 border border-white/20">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#E1C582] rounded-full flex items-center justify-center mb-2 sm:mb-3">
                  <span className="text-xl sm:text-2xl font-bold text-[#1C2E5D]">2</span>
                </div>
                <h3 className="font-semibold text-base sm:text-lg mb-1 sm:mb-2">Share Your Experience</h3>
                <p className="text-xs sm:text-sm text-blue-100">Tell us what works and what doesn't</p>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 sm:p-5 border border-white/20 sm:col-span-2 md:col-span-1">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#E1C582] rounded-full flex items-center justify-center mb-2 sm:mb-3">
                  <span className="text-xl sm:text-2xl font-bold text-[#1C2E5D]">3</span>
                </div>
                <h3 className="font-semibold text-base sm:text-lg mb-1 sm:mb-2">Make an Impact</h3>
                <p className="text-xs sm:text-sm text-blue-100">Your feedback helps improve processes for everyone</p>
              </div>
            </div>

            <div className="mt-6 sm:mt-8 flex items-center gap-2 text-xs sm:text-sm text-blue-100">
              <i className="ri-lock-line text-base sm:text-lg"></i>
              <span>100% anonymous • No login required • Takes 3-5 minutes</span>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-2">
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">Process Catalogue</h2>
          </div>
          <p className="text-base sm:text-lg text-gray-600">
            Select a category below to explore processes and share your feedback
          </p>
        </div>

        {/* Search Bar */}
        <div className="mb-6 sm:mb-8">
          <div className="relative max-w-2xl">
            <div className="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none">
              <i className="ri-search-line text-lg sm:text-xl text-gray-400"></i>
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search for a process..."
              className="w-full pl-10 sm:pl-12 pr-10 sm:pr-12 py-3 sm:py-4 text-sm border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1C2E5D] focus:border-transparent transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute inset-y-0 right-0 pr-3 sm:pr-4 flex items-center cursor-pointer hover:opacity-70 transition-opacity"
              >
                <i className="ri-close-circle-fill text-lg sm:text-xl text-gray-400"></i>
              </button>
            )}
          </div>

          {/* Search Results */}
          {searchQuery.trim() && (
            <div className="mt-4 bg-white rounded-xl border-2 border-[#E1C582] shadow-lg p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm sm:text-base font-semibold text-gray-900">
                  {searchResults.length > 0 
                    ? `Found ${searchResults.length} process${searchResults.length !== 1 ? 'es' : ''}`
                    : 'No processes found'
                  }
                </h3>
                {searchResults.length > 0 && (
                  <span className="text-xs text-gray-500 hidden sm:inline">Click to review</span>
                )}
              </div>
              
              {searchResults.length > 0 ? (
                <div className="grid gap-3">
                  {searchResults.map((process) => (
                    <Link
                      key={process.id}
                      to={`/process/${process.id}`}
                      className="group flex items-start gap-2 sm:gap-3 px-3 sm:px-4 py-3 sm:py-3.5 bg-gradient-to-r from-[#E1C582]/10 to-transparent rounded-lg hover:from-[#E1C582]/20 hover:to-[#E1C582]/10 transition-all cursor-pointer border border-[#E1C582]/30 hover:border-[#E1C582] hover:shadow-md"
                    >
                      <i className="ri-arrow-right-circle-fill text-lg sm:text-xl text-[#1C2E5D] flex-shrink-0 mt-0.5"></i>
                      <div className="flex-1 min-w-0">
                        <span className="text-sm text-gray-900 group-hover:text-[#1C2E5D] font-medium block">
                          {process.name}
                        </span>
                        <div className="flex items-center gap-2 sm:gap-3 mt-1">
                          {renderStars(process.rating)}
                          <span className={`text-xs font-semibold ${getRatingColor(process.rating)}`}>
                            {process.rating.toFixed(1)}
                          </span>
                          <span className="text-xs text-gray-500">
                            {process.responses.toLocaleString()} responses
                          </span>
                        </div>
                        <span className="text-xs text-gray-500 mt-1 block">
                          {process.categoryName}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <i className="ri-search-line text-3xl sm:text-4xl text-gray-300 mb-3"></i>
                  <p className="text-sm text-gray-500">
                    No processes match "{searchQuery}"
                  </p>
                  <p className="text-xs text-gray-400 mt-2">
                    Try different keywords or browse categories below
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Tab Switcher - 5 Groups */}
        <div className="mb-6 sm:mb-8 overflow-x-auto">
          <div className="inline-flex bg-gray-100 rounded-full p-1 min-w-full sm:min-w-0 gap-1">
            <button
              onClick={() => {
                setActiveTab('student');
                setExpandedCategory(null);
              }}
              className={`flex-1 sm:flex-none px-3 sm:px-5 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeTab === 'student'
                  ? 'bg-[#1C2E5D] text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <i className="ri-user-line mr-1 sm:mr-2"></i>
              Student Services
            </button>
            <button
              onClick={() => {
                setActiveTab('academic');
                setExpandedCategory(null);
              }}
              className={`flex-1 sm:flex-none px-3 sm:px-5 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeTab === 'academic'
                  ? 'bg-[#1C2E5D] text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <i className="ri-flask-line mr-1 sm:mr-2"></i>
              Academic
            </button>
            <button
              onClick={() => {
                setActiveTab('admin');
                setExpandedCategory(null);
              }}
              className={`flex-1 sm:flex-none px-3 sm:px-5 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeTab === 'admin'
                  ? 'bg-[#1C2E5D] text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <i className="ri-building-line mr-1 sm:mr-2"></i>
              Operations
            </button>
            <button
              onClick={() => {
                setActiveTab('finance');
                setExpandedCategory(null);
              }}
              className={`flex-1 sm:flex-none px-3 sm:px-5 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeTab === 'finance'
                  ? 'bg-[#1C2E5D] text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <i className="ri-money-dollar-circle-line mr-1 sm:mr-2"></i>
              Finance
            </button>
            <button
              onClick={() => {
                setActiveTab('governance');
                setExpandedCategory(null);
              }}
              className={`flex-1 sm:flex-none px-3 sm:px-5 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeTab === 'governance'
                  ? 'bg-[#1C2E5D] text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <i className="ri-shield-check-line mr-1 sm:mr-2"></i>
              Governance & IT
            </button>
          </div>
        </div>

        {/* Categories */}
        <div className="space-y-3 sm:space-y-4">
          {filteredCategories.length > 0 ? (
            filteredCategories.map((category) => {
              const stats = getCategoryStats(category);
              return (
                <div key={category.id} className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow">
                  <button
                    onClick={() => toggleCategory(category.id)}
                    className="w-full px-4 sm:px-6 py-4 sm:py-5 flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#E1C582]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className={`ri-${expandedCategory === category.id ? 'folder-open' : 'folder'}-line text-lg sm:text-xl text-[#1C2E5D]`}></i>
                      </div>
                      <div className="text-left min-w-0 flex-1">
                        <span className="text-sm sm:text-base font-semibold text-gray-900 block truncate">{category.name}</span>
                        <div className="flex items-center gap-2 sm:gap-3 mt-1">
                          <div className="flex items-center gap-0.5">
                            {renderStars(stats.avgRating)}
                          </div>
                          <span className={`text-xs font-semibold ${getRatingColor(stats.avgRating)}`}>
                            {stats.avgRating.toFixed(1)}
                          </span>
                          <span className="text-xs text-gray-500 hidden sm:inline">
                            {stats.totalResponses.toLocaleString()} responses
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                      <span className="text-xs sm:text-sm text-gray-500 hidden sm:inline">{category.processes.length} processes</span>
                      <i className={`ri-arrow-${expandedCategory === category.id ? 'up' : 'down'}-s-line text-lg sm:text-xl text-gray-400`}></i>
                    </div>
                  </button>
                  
                  {expandedCategory === category.id && (
                    <div className="border-t border-gray-200 bg-gradient-to-b from-gray-50 to-white">
                      <div className="px-4 sm:px-6 py-4 sm:py-5 grid gap-3">
                        {category.processes.map((process) => (
                          <Link
                            key={process.id}
                            to={`/process/${process.id}`}
                            className="group flex items-start gap-2 sm:gap-3 px-3 sm:px-4 py-3 sm:py-3.5 bg-white rounded-lg hover:bg-[#E1C582]/10 hover:border-[#E1C582] transition-all cursor-pointer border border-gray-200 shadow-sm"
                          >
                            <i className="ri-arrow-right-circle-line text-lg sm:text-xl text-gray-400 group-hover:text-[#1C2E5D] transition-colors flex-shrink-0 mt-0.5"></i>
                            <div className="flex-1 min-w-0">
                              <span className="text-sm text-gray-900 group-hover:text-[#1C2E5D] font-medium block mb-1.5">
                                {process.name}
                              </span>
                              <div className="flex items-center gap-2 sm:gap-3">
                                {renderStars(process.rating)}
                                <span className={`text-xs font-semibold ${getRatingColor(process.rating)}`}>
                                  {process.rating.toFixed(1)}
                                </span>
                              </div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            !searchQuery && (
              <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
                <i className="ri-folder-open-line text-3xl sm:text-4xl text-gray-300 mb-3"></i>
                <p className="text-sm text-gray-500">No categories available</p>
              </div>
            )
          )}
        </div>

        {/* Call to Action */}
        <div className="mt-8 sm:mt-12 bg-gradient-to-r from-[#E1C582]/20 to-[#E1C582]/10 rounded-xl p-6 sm:p-8 border border-[#E1C582]/30">
          <div className="flex flex-col sm:flex-row items-start gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#1C2E5D] rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ri-lightbulb-line text-xl sm:text-2xl text-[#E1C582]"></i>
            </div>
            <div className="flex-1">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">Your Voice Matters</h3>
              <p className="text-sm text-gray-600 mb-4">
                Every piece of feedback helps us identify which processes need improvement, digitization, or simplification. 
                Your honest input creates real change.
              </p>
              <div className="flex flex-wrap gap-3 sm:gap-4 text-xs sm:text-sm text-gray-700">
                <div className="flex items-center gap-2">
                  <i className="ri-check-line text-[#1C2E5D]"></i>
                  <span>Anonymous feedback</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-check-line text-[#1C2E5D]"></i>
                  <span>Quick & easy</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-check-line text-[#1C2E5D]"></i>
                  <span>Real impact</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-50 border-t border-gray-200 mt-12 sm:mt-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <p className="text-xs sm:text-sm text-gray-500 text-center sm:text-left">
            © 2025 Babcock University. All feedback is anonymous.
          </p>
        </div>
      </footer>
    </div>
  );
}
